import * as React from 'react';

import once from 'lodash/once';

import { BaseParams } from '@common/react/objects/BaseParams';

export interface IModal {
	onClose?: VoidFunction;
	visible?: boolean;
}

export interface AlertProps extends IModal {
	title?: string;
	content?: string;
	onOk?: VoidFunction;
}

export interface ConfirmProps extends IModal {
	title?: string;
	content?: string;
	onOk?: VoidFunction;
	onCancel?: VoidFunction;
}

export type OpenModal<T> = (params: T) => void;

interface IModalContext<T extends BaseParams = BaseParams, CType extends BaseParams = BaseParams> {
	openAlert: OpenModal<AlertProps & T>;
	openConfirm: OpenModal<ConfirmProps & CType>;
	openDeleteConfirm: OpenModal<ConfirmProps & CType>;
	openError: OpenModal<AlertProps>;
	openSuccess: OpenModal<AlertProps>;
	renderModal: (visible: boolean, close: (resetProps?: boolean) => void, props: BaseParams) => React.ReactNode;
	openErrorMessage?: (message: string, params?) => void;
	openSuccessMessage?: (message: string, params?) => void;
}

export const createModalContext = once(<T extends BaseParams, CType extends BaseParams>() => React.createContext({} as IModalContext<T, CType>));

export const useModal = <
	T extends BaseParams = BaseParams,
	CType extends BaseParams = BaseParams,
>() => React.useContext<IModalContext<T, CType>>(createModalContext<T, CType>());

const useDefaultModalLogic = <T extends BaseParams = BaseParams>() => {
	const [visible, setVisible] = React.useState(false);
	const [props, setProps] = React.useState<T | undefined>();

	const openModal = React.useCallback((props?: T) => {
		setProps(props);
		setVisible(true);
	}, []);

	const closeModal = React.useCallback((resetProps: boolean = true) => {
		resetProps && setProps(undefined);
		setVisible(false);
	}, []);

	const updateProps = (props) => {
		setProps((prev) => (prev ? { ...prev, ...props } : props));
	};

	return {
		visible,
		props,
		openModal,
		updateProps,
		closeModal,
	};
};

/**
 * This is the description of the interface
 *
 * @interface ModalContextProviderProps
 * @typeParam AlertProps - AlertProps Any BaseParams entity
 * @typeParam ConfirmProps - ConfirmProps Any BaseParams entity
 */
interface ModalContextProviderProps<AlertProps extends BaseParams = BaseParams, ConfirmProps extends BaseParams = BaseParams> {
	/**
	 * function to render Alert
	 * @param visible - alert visibility
	 * @param close - callback to close alert
	 * @param props - alert props
	 */
	renderAlert: (visible: boolean, close: VoidFunction, props: AlertProps) => React.ReactNode;
	/**
	 * function to render Success
	 * @param visible - success visibility
	 * @param close - callback to close success
	 * @param props - success props
	 */
	renderSuccess: (visible: boolean, close: VoidFunction, props: BaseParams) => React.ReactNode;
	/**
	 * function to render Error
	 * @param visible - error visibility
	 * @param close - callback to close error
	 * @param props - error props
	 */
	renderError: (visible: boolean, close: VoidFunction, props: BaseParams) => React.ReactNode;
	/**
	 * function to render Confirm
	 * @param visible - confirm visibility
	 * @param close - callback to close confirm
	 * @param props - confirm props
	 */
	renderConfirm: (visible: boolean, close: VoidFunction, props: ConfirmProps) => React.ReactNode;
	/**
	 * function to render Modal
	 * @param visible - modal visibility
	 * @param close - callback to close modal
	 * @param props - modal props
	 */
	renderModal: (visible: boolean, close: (resetProps?: boolean) => void, props: BaseParams) => React.ReactNode;
	/**
	 * function to render DeleteConfirm
	 * @param visible - DeleteConfirm visibility
	 * @param close - callback to close DeleteConfirm
	 * @param props - DeleteConfirm props
	 */
	renderDeleteConfirm: (visible: boolean, close: VoidFunction, props: ConfirmProps) => React.ReactNode;
	children?: React.ReactNode;
	/**
	 * function to render ErrorMessage
	 * @param message - error message
	 * @param params - errorMessage props
	 */
	openErrorMessage?: (message: string, params: any) => void;
	/**
	 * function to render SuccessMessage
	 * @param message - Success message
	 * @param params - SuccessMessage props
	 */
	openSuccessMessage?: (message: string, params: any) => void;
}

/**
 * ModalContextProvider component.
 *
 * usage examples:
 *  - <ItemsProvider type="someType">{React.ReactNode}</ItemsProvider>
 *
 * @typeParam IAlertProps - IAlertProps Any AlertProps
 * @typeParam IConfirmProps - IConfirmProps Any ConfirmProps
 * @param props - ModalContextProviderProps<IAlertProps, IConfirmProps>
 * @returns React.ReactElement
 */
export const ModalContextProvider = <IAlertProps extends AlertProps = AlertProps, IConfirmProps extends ConfirmProps = ConfirmProps>({
	children,
	renderAlert,
	renderConfirm,
	renderDeleteConfirm,
	renderModal,
	renderError,
	renderSuccess,
	openErrorMessage,
	openSuccessMessage,
}: ModalContextProviderProps<IAlertProps, IConfirmProps>) => {
	const ModalContext = createModalContext<IAlertProps, IConfirmProps>();
	const {
		openModal: openAlert,
		closeModal: closeAlert,
		props: alertProps,
		visible: alertVisible,
	} = useDefaultModalLogic<IAlertProps>();
	const {
		openModal: openSuccess,
		closeModal: closeSuccess,
		props: successProps,
		visible: successVisible,
	} = useDefaultModalLogic<BaseParams>();
	const {
		openModal: openError,
		closeModal: closeError,
		props: errorProps,
		visible: errorVisible,
	} = useDefaultModalLogic<BaseParams>();
	const {
		openModal: openConfirm,
		closeModal: closeConfirm,
		props: confirmProps,
		visible: confirmVisible,
	} = useDefaultModalLogic<IConfirmProps>();
	const {
		openModal: openDeleteConfirm,
		closeModal: closeDeleteConfirm,
		props: deleteConfirmProps,
		visible: deleteConfirmVisible,
	} = useDefaultModalLogic<IConfirmProps>();

	const modalContextValue: IModalContext<IAlertProps, IConfirmProps> = {
		openAlert,
		openConfirm,
		renderModal,
		openError,
		openSuccess,
		openErrorMessage,
		openSuccessMessage,
		openDeleteConfirm,
	};

	return (
		<ModalContext.Provider value={modalContextValue}>
			{alertProps && (
				renderAlert(alertVisible, closeAlert, alertProps)
			)}
			{errorProps && (
				renderError(errorVisible, closeError, errorProps)
			)}
			{successProps && (
				renderSuccess(successVisible, closeSuccess, successProps)
			)}
			{confirmProps && (
				renderConfirm(confirmVisible, closeConfirm, confirmProps)
			)}
			{deleteConfirmProps && (
				renderDeleteConfirm(deleteConfirmVisible, closeDeleteConfirm, deleteConfirmProps)
			)}
			{children}
		</ModalContext.Provider>
	);
};
